import React, { createContext, useContext, useState, useEffect } from 'react';

const PreferencesContext = createContext();

export const usePreferences = () => {
  const context = useContext(PreferencesContext);
  if (!context) {
    throw new Error('usePreferences must be used within PreferencesProvider');
  }
  return context;
};

export const PreferencesProvider = ({ children }) => {
  const [preferences, setPreferences] = useState(() => {
    const savedTheme = localStorage.getItem('theme') || 'system';
    return {
      theme: savedTheme,
      compactMode: false,
      animations: true,
      language: 'English',
      timezone: 'UTC-5',
      dateFormat: 'MM/DD/YYYY',
      timeFormat: '12h',
      weekStart: 'Sunday',
      emailNotifications: true,
      pushNotifications: true,
      desktopNotifications: false,
      notificationSound: true,
      itemsPerPage: 20,
      defaultView: 'grid',
      showRecentItems: true,
      autoRefresh: true,
      refreshInterval: '30',
      highContrast: false,
      fontSize: 'medium',
      reducedMotion: false
    };
  });

  const [isDarkMode, setIsDarkMode] = useState(() => {
    const t = localStorage.getItem('theme') || 'system';
    if (t === 'dark') return true;
    if (t === 'light') return false;
    return typeof window !== 'undefined' ? window.matchMedia('(prefers-color-scheme: dark)').matches : false;
  });

  // Apply theme to document
  const applyTheme = (theme) => {
    const root = document.documentElement;
    let isDark = false;

    if (theme === 'dark') {
      isDark = true;
    } else if (theme === 'light') {
      isDark = false;
    } else {
      // 'system' mode
      isDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    }

    if (isDark) {
      root.classList.add('dark');
      root.style.colorScheme = 'dark';
      root.setAttribute('data-theme', 'dark');
    } else {
      root.classList.remove('dark');
      root.style.colorScheme = 'light';
      root.setAttribute('data-theme', 'light');
    }

    setIsDarkMode(isDark);
    localStorage.setItem('theme', theme);
  };

  // Apply font size
  const applyFontSize = (fontSize) => {
    const sizeMap = {
      small: '14px',
      medium: '16px',
      large: '18px'
    };
    document.documentElement.style.fontSize = sizeMap[fontSize] || '16px';
  };

  // Apply compact mode
  const applyCompactMode = (enabled) => {
    if (enabled) {
      document.documentElement.classList.add('compact-mode');
    } else {
      document.documentElement.classList.remove('compact-mode');
    }
  };

  // Apply reduced motion
  const applyReducedMotion = (enabled) => {
    if (enabled) {
      document.documentElement.classList.add('reduced-motion');
    } else {
      document.documentElement.classList.remove('reduced-motion');
    }
  };

  // Apply high contrast
  const applyHighContrast = (enabled) => {
    if (enabled) {
      document.documentElement.classList.add('high-contrast');
    } else {
      document.documentElement.classList.remove('high-contrast');
    }
  };

  // Apply animations
  const applyAnimations = (enabled) => {
    if (!enabled) {
      document.documentElement.classList.add('disable-animations');
    } else {
      document.documentElement.classList.remove('disable-animations');
    }
  };

  // Apply all preferences
  const applyAllPreferences = (prefs) => {
    applyTheme(prefs.theme);
    applyFontSize(prefs.fontSize);
    applyCompactMode(prefs.compactMode);
    applyReducedMotion(prefs.reducedMotion);
    applyHighContrast(prefs.highContrast);
    applyAnimations(prefs.animations);
  };

  // Load preferences on mount
  useEffect(() => {
    const loadPreferences = () => {
      try {
        const savedTheme = localStorage.getItem('theme');
        const savedPreferences = localStorage.getItem('userPreferences') || localStorage.getItem('preferences');
        let initialPrefs = preferences;
        if (savedPreferences) {
          initialPrefs = { ...initialPrefs, ...JSON.parse(savedPreferences) };
        }
        if (savedTheme) {
          initialPrefs.theme = savedTheme;
        }
        setPreferences(initialPrefs);
        applyAllPreferences(initialPrefs);
      } catch (error) {
        console.error('Error loading preferences:', error);
      }
    };
    
    loadPreferences();

    // Listen for system theme changes
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleSystemThemeChange = (e) => {
      const currentTheme = localStorage.getItem('theme') || preferences.theme || 'system';
      if (currentTheme === 'system') {
        const root = document.documentElement;
        if (e.matches) {
          root.classList.add('dark');
          root.style.colorScheme = 'dark';
          root.setAttribute('data-theme', 'dark');
          setIsDarkMode(true);
        } else {
          root.classList.remove('dark');
          root.style.colorScheme = 'light';
          root.setAttribute('data-theme', 'light');
          setIsDarkMode(false);
        }
      }
    };

    // Cross-tab theme synchronization
    const handleStorageChange = (e) => {
      if (e.key === 'theme' && e.newValue) {
        applyTheme(e.newValue);
        setPreferences((prev) => ({ ...prev, theme: e.newValue }));
      }
    };
    
    mediaQuery.addEventListener('change', handleSystemThemeChange);
    window.addEventListener('storage', handleStorageChange);
    return () => {
      mediaQuery.removeEventListener('change', handleSystemThemeChange);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // Update a single preference
  const updatePreference = (key, value) => {
    const newPreferences = { ...preferences, [key]: value };
    setPreferences(newPreferences);
    
    // Apply real-time changes
    switch (key) {
      case 'theme':
        applyTheme(value);
        break;
      case 'fontSize':
        applyFontSize(value);
        break;
      case 'compactMode':
        applyCompactMode(value);
        break;
      case 'reducedMotion':
        applyReducedMotion(value);
        break;
      case 'highContrast':
        applyHighContrast(value);
        break;
      case 'animations':
        applyAnimations(value);
        break;
      default:
        break;
    }
    
    // Save to localStorage under all standard keys
    localStorage.setItem(key, value);
    localStorage.setItem('userPreferences', JSON.stringify(newPreferences));
    localStorage.setItem('preferences', JSON.stringify(newPreferences));
    
    // Dispatch events for other components
    window.dispatchEvent(new CustomEvent('preferencesUpdated', { detail: newPreferences }));
    if (key === 'theme') {
      window.dispatchEvent(new CustomEvent('themeChanged', { detail: { theme: value } }));
    }
  };

  const setTheme = (newTheme) => {
    updatePreference('theme', newTheme);
  };

  // Update multiple preferences
  const updatePreferences = (newPreferences) => {
    setPreferences(newPreferences);
    applyAllPreferences(newPreferences);
    localStorage.setItem('userPreferences', JSON.stringify(newPreferences));
    localStorage.setItem('preferences', JSON.stringify(newPreferences));
    window.dispatchEvent(new CustomEvent('preferencesUpdated', { detail: newPreferences }));
    if (newPreferences.theme) {
      window.dispatchEvent(new CustomEvent('themeChanged', { detail: { theme: newPreferences.theme } }));
    }
  };

  return (
    <PreferencesContext.Provider value={{ preferences, updatePreference, updatePreferences, theme: preferences.theme, setTheme, isDarkMode }}>
      {children}
    </PreferencesContext.Provider>
  );
};